import { useCallback, useRef, useMemo } from 'react';
import { useState, useEffect } from "react";
import { Button, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Chart, Area, Tooltip, Axis, Legend } from "bizcharts";
import UtilService from '../../../../../../helper/service';
import LiveAnnotation from "../live/annotation";
import MarkerSection from "../completed/notes/markerSection";
import clsx from "clsx";
import BackgroundLines from '../completed/graph/bglines';
import ScorePane from '../completed/graph/scorePane';
import _ from "underscore";
import { updateSettings } from "../../../../../../store/actions/creator/analysis";
import useWindowSize from '../../../../../../helper/hooks/useWindowSize';
import constants from '../../../../../../config/constants';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'center',
    width: '100%',
    marginTop: 30,
  },
  container: {
    position: 'relative',
    marginTop: 50,
    height: 200,
  },
  bottomContainer: {
    position: 'fixed',
    display: 'flex',
    justifyContent: 'flex-start',
    bottom: 20
  },
  score: {
    position: 'absolute',
    marginLeft: -60,
  },
  graphPane: {
    height: '100%',
    position: 'relative',
    transition: '1s',
  },
  postBtn: {
    height: 37,
    borderRadius: 6,
    background: '#0179EE',
    color: 'white',
    textTransform: 'none',
    fontSize: 14,
    fontFamily: 'roboto-regular',
    '&:hover': {
      background: '#0d77d4',
    },
  },
  chartH: {
    '& canvas': {
      height: '200px!important',
    }
  },
  fixedPane: {
    position: 'fixed',
    zIndex: 5,
    background: '#F0F0F0',
  },
  hiddenPane: {
    marginTop: -3,
    position: 'absolute',
    background: '#f0f0f0',
    height: 70,
    width: 'calc(100% + 120px)',
    left: -60
  }
}));

function LiveSection(props: any) {

  const classes = useStyles();
  const dispatch = useDispatch();
  const annotationRef: any = useRef();

  const { all, analyseStart, analyseEnd, realTimeGraphType, liveFirstLoadTime, settings, currentSession } = useSelector((state: any) => state.analysis);
  const { graphZoom } = settings;
  const { session_type, started_at } = currentSession;
  const { onNoteScroll } = props;

  const [addNoteX, setAddNoteX] = useState(-1);
  const [addNoteY, setAddNoteY] = useState(-1);
  const [annotation, setAnnotation] = useState('');
  const [notePostBox, setNotePostBox] = useState(false);
  const [count, setCount] = useState<number>(0);

  const graphLength = useMemo(() => {
    return (analyseEnd - analyseStart) > 0 ? analyseEnd - analyseStart : 100
  }, [analyseEnd, analyseStart]);

  const getMaxWidth = Math.floor(graphLength / Math.pow(graphZoom, 2) * 15);

  const { width } = useWindowSize();
  const paneWidth = (width && width > 1200) ? (width - 600) : 600;
  const lineNumber = getMaxWidth / 15;

  const customGraphEnd = useMemo(() => {
    return analyseEnd > 30 ? analyseEnd : 30
  }, [analyseEnd])

  const endGraphPoint = liveFirstLoadTime + count - 0;

  const scale = useMemo(() => {
    return {
      value: {
        min: 0,
        max: 103,
        nice: true,
      },
      timestamp: (session_type === 'ASYNC')
        ? {}
        : {
          min: analyseStart,
          max: customGraphEnd + (UtilService.getNowLocalTime() - Number(started_at) > 300 ? 10 : 0),
        },
    }
  }, [customGraphEnd])

  useEffect(() => {
    if (analyseEnd && analyseStart && graphLength) {
      dispatch(updateSettings('graphZoom', Math.sqrt(graphLength * 15 / paneWidth)));
    }
  }, [analyseEnd, analyseStart, width]);

  useEffect(() => {
    if (realTimeGraphType === 1) {
      const myIntervalOverall = setInterval(() => {
        setCount(count + 1)
      }, constants.ANALYZE.LIVE_MOD_REAL_TIME)
      return () => {
        clearInterval(myIntervalOverall);
      };
    } else {
      const myIntervalOverall = setInterval(() => {
        setCount(UtilService.getNowLocalTime() - liveFirstLoadTime - 10)
      }, constants.ANALYZE.LIVE_MOD_OVERALL_TIME);
      return () => {
        clearInterval(myIntervalOverall);
      };
    }
  });

  useEffect(() => {
    setCount(UtilService.getNowLocalTime() - liveFirstLoadTime - 10)
  }, [analyseEnd]);

  const annotationFocus = useCallback(() => {
    if (!annotationRef?.current) return;
    annotationRef.current.onFocusInput();
  }, [annotationRef]);

  const onPostAnnotation = (start_at: any, annotation: any) => {
    const newY = (addNoteY + 90) % 630;
    setAddNoteX(start_at);
    setAddNoteY(newY);
    setAnnotation(annotation);
    onNoteScroll(all.length * 220 + Math.min(Math.max(Math.round(newY / 90), 0), 6) * 90)
  }

  const filteredData = all
    .map((item: any) => {
      const sortData = (item.immersion.data ? item.immersion.data : [])
        .filter((x: any) => UtilService.getLocalEpoch2(x.time_stamp) < endGraphPoint)
        .map((c: any) => {
          return {
            value: c.immersion_index,
            timestamp: UtilService.getLocalEpoch2(c.time_stamp)
          }
        })
      return {
        ...item,
        immersion: {
          ...item.immersion,
          data: _.uniq(sortData, function (x) { return x.timestamp })
            .reduce((prev: any[], c: any, index: number) => {
              if (index < item.immersion.data.length && index > 0) {
                const timeDiff = Math.abs(c.timestamp - sortData[index - 1].timestamp)
                if (timeDiff > 1) {
                  for (let i = 1; i < timeDiff - 1; i++) {
                    const duplicatedIndex = sortData.findIndex((item: any) => item.timestamp === sortData[index - 1].timestamp + i);
                    duplicatedIndex === -1 && prev.push({
                      value: 0,
                      timestamp: sortData[index - 1].timestamp + i
                    })
                  }
                }
              }
              prev.push(c)
              return prev
            }, [])
            .sort(function (a: any, b: any) {
              return a.timestamp - b.timestamp
            })
        }
      }
    });

  return (
    <div className={classes.root} 
    >
      <BackgroundLines
        graphType={'LIVE'}
        getMaxWidth={getMaxWidth}
        lineNumber={lineNumber}
        graphZoom={graphZoom}
      />

      <div style={{ width: getMaxWidth }}>
        <br />
        {
          filteredData[0] &&
          filteredData[0].activeParticipant === 'All' &&
          <div className={classes.fixedPane} style={{ width: paneWidth }}>
            <div className={classes.hiddenPane} />
            <BackgroundLines
              getMaxWidth={getMaxWidth}
              paneWidth={paneWidth}
              allParticipants={true}
              live={true}
            />
            <LiveGraphItem
              style={{ zIndex: 5, position: 'relative' }}
              scoreStyle={{ height: 200 }}
              data={{ scale, item: filteredData[0], getMaxWidth }}
              endGraphPoint={endGraphPoint}
            />
          </div>}

        {
          filteredData.map((item: any, key: number) =>
            <LiveGraphItem
              endGraphPoint={endGraphPoint}
              data={{ scale, item, getMaxWidth }}
              key={key} />)
        }
      </div>

      <div
        className={clsx(classes.graphPane)}
        style={{ width: getMaxWidth }}
      >
        <div
          style={{ width: getMaxWidth, marginTop: 30 }}
          id={"note_scroll"}
        >
          <MarkerSection
            addNoteX={addNoteX}
            addNoteY={addNoteY}
            editDone={annotationFocus}
            graphZoom={graphZoom}
            annotation={annotation}
            liveMode={true}
            graphWidth={getMaxWidth}
            lineStep={15} />
        </div>

        <div className={classes.bottomContainer} style={{ width: getMaxWidth }}>

          {
            !notePostBox &&
            <Button
              variant="contained"
              color="secondary"
              className={classes.postBtn}
              onClick={() => setNotePostBox(true)}
            >
              Add Note
            </Button>
          }

          {
            notePostBox &&
            <LiveAnnotation
              ref={annotationRef}
              onAddNote={(start_at: any, annotation: any) => onPostAnnotation(start_at, annotation)}
              onCloseBox={() => setNotePostBox(false)}
              sessionType={session_type}
              analyseStart={analyseStart}
            />
          }

        </div>

      </div>
    </div>
  );
}

export default LiveSection;

function LiveGraphItem(props: any) {

  const classes = useStyles();

  const { style, data, scoreStyle, endGraphPoint } = props;
  const { scale, item, getMaxWidth } = data;
  const isAll = item.activeParticipant === 'All';
  const findIndex = item.immersion.data.findIndex((x: any) => x.timestamp === endGraphPoint - 1);
  const customIndex = findIndex >= 0 ? item.immersion.data[findIndex].value : 0;

  const rightScore = {
    immersion_index: customIndex,
    color_indicator: UtilService.getColorFromImmersion2(customIndex),
    context_indicator: item.immersion.overall_context_indicator
  }

  return (
    <div className={classes.container} style={style}>
      <div className={classes.score} style={scoreStyle}>
        {item?.immersion.data && <ScorePane
          sessionStatus={'LIVE'}
          immersionData={item?.immersion}
          participant={item?.activeParticipant}
          graphWidth={getMaxWidth}
          overall={item?.overall_immersion}
          realTimeScores={rightScore}
        />}
      </div>
      <Chart
        className={classes.chartH}
        scale={scale}
        data={item?.immersion.data}
        animate={false}
        autoFit
      >
        <Axis name="value" visible={false} />
        <Axis name="timestamp" visible={false} />
        <Legend visible={false} />
        <Tooltip visible={false} />

        {
          ((!isAll && !style) || style) && // hide all participant graph behind
          <Area
            position="timestamp*value" color={'grey'} animate={{
              enter: {
                duration: 1000,
              },
              leave: false,
            }} />}
      </Chart>
    </div>
  )
}